import { ensureNotNil } from '@studio/js-utils';
import type { Brand, Device, Model } from '@studio/models/device';
import type { CameraParameters } from '@tryiris-ai/common.protojs/api/v1/camera_calibration';
import type {
  DeviceRegistryBrand,
  DeviceRegistryModel
} from '@tryiris-ai/common.protojs/api/v1/device_registry';
import { DeviceRegistryModelFidelityLabel } from '@tryiris-ai/common.protojs/api/v1/device_registry';
import { toString, transformArray } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import {
  DeviceRegistryBrandList,
  DeviceRegistryGetCalibration,
  DeviceRegistryGetConfiguration,
  DeviceRegistryModelsList
} from '@tryiris-ai/mailroom-helper/deviceRegistry';
import { StatusCode } from 'status-code-enum';
import { DeviceType } from './types.js';
import type { Collection, Registrant } from './types.js';
import { toHexFromId, toIdFromHex, wrapApiCall } from '#~/api/index.js';
import type { ByteId, OmitSyncProps } from '#~/api/index.js';
// ApiError is only re-exported as a TYPE from '#~/api/index.js', so the class
// value comes from types.js directly (matches api/agent/registry.ts).
import { ApiError } from '#~/api/types.js';
import { nats } from '#~/nats/index.js';

/**
 * ENG-4012: guard against leaking a nil modelId onto the wire. Unresolved NDI
 * sources reach the registry fetchers with an empty/undefined model id; the
 * lambda then answers 404 "model not found", which surfaces as a noisy,
 * misleading [API-404] toast. Short-circuit with a typed 400 BEFORE any wire
 * call instead. Per `stay-in-scope` (ai.knowledge/global) the watchdog caller
 * is untouched — this is the smallest-blast-radius fix.
 *
 * @param modelId - Model identifier (hex string).
 * @param what - Human-readable name of the thing being fetched, for the error.
 * @returns The model id as a byte array.
 * @throws ApiError (400) when the model id is empty/undefined/malformed.
 */
const requireModelIdBytes = (modelId: T_Maybe<Model['id']>, what: string): ByteId => {
  const bytes = toIdFromHex(modelId);

  if (!bytes) {
    throw new ApiError(`${what} requires a model id`, {
      code: StatusCode.ClientErrorBadRequest
    });
  }

  return bytes;
};

/** @internal */
type Comparable = Readonly<{
  generic: boolean;
  name: string;
  /** ENG-4924: only models carry this; brands leave it undefined. */
  notYetSupported?: boolean;
}>;

/**
 * ENG-4924: whether an entry is the brand's generic template ("Other"). A not-yet-supported model
 * also has `generic = true` (registry invariant) but is a named camera, not the template. It must
 * not sort into the generic tail and must never become the brand's generic fallback.
 *
 * @internal
 */
const isGenericTemplate = (µ: Comparable): boolean => µ.generic && !µ.notYetSupported;

/** @internal */
const comparator = (a: Comparable, b: Comparable): number => {
  if (isGenericTemplate(a)) {
    return isGenericTemplate(b) ? 0 : 1;
  }

  // Bug fix: the original code did not handle the case where only `b` is the generic template.
  // That was harmless while every generic was "Other", but a not-yet-supported `a` can now be
  // compared against the template `b`, and the template must still sort last.
  if (isGenericTemplate(b)) {
    return -1;
  }

  return a.name.localeCompare(b.name);
};

/**
 * Fetch the available devices brands.
 *
 * @param token - Access token.
 * @param deviceType - Device type (default `Camera`).
 * @returns Brand collection.
 */
export const fetchBrands = async (
  token: string,
  deviceType: DeviceType = DeviceType.Camera
): Promise<Collection<Brand>> => {
  // W12 — `devices` diagnostic endpoint hint. The brand list is the
  // top of the device-registry diagnostic surface (driver/model
  // discovery); logging the full body lets support reconstruct what
  // device choices the renderer believed it had at incident time.
  const { brands } = await wrapApiCall(
    async () =>
      DeviceRegistryBrandList.request(nats.cloudConnection(), {
        deviceType,
        token
      }),
    undefined,
    'devices'
  );

  brands.sort(comparator);

  let generic: T_Maybe<Brand>;

  const entries = transformArray(brands, {
    transformer: (µ): T_Maybe<[Brand['id'], Brand]> => {
      const brand = toBrand(µ);

      if (!brand) {
        return undefined;
      }

      if (µ.generic) {
        generic = brand;
      }

      return [brand.id, brand];
    }
  });

  return { generic: ensureNotNil(generic), items: Object.fromEntries(entries) };
};

/**
 * Fetch the calibration profile specified device model.
 *
 * @param modelId - Model identifier.
 * @param deviceId - Device identifier.
 * @param token - Access token.
 * @returns Calibration profile.
 */
export const fetchCalibration = async (
  modelId: Model['id'],
  deviceId: Device['id'],
  token: string
): Promise<CameraParameters> => {
  // ENG-4012 — never let a nil modelId reach the wire (see requireModelIdBytes).
  const modelIdBytes = requireModelIdBytes(modelId, 'Calibration');

  // W12 — pass the diagnostic endpoint hint to wrapApiCall so the
  // central observability emitter logs `api.response endpoint=calibration`
  // with the (W3-redacted) full body. See logger/observability.ts.
  const { cameraParameters } = await wrapApiCall(
    async () =>
      DeviceRegistryGetCalibration.request(nats.cloudConnection(), {
        deviceId: toIdFromHex(deviceId)!,
        modelId: modelIdBytes,
        token
      }),
    undefined,
    'calibration'
  );

  if (!cameraParameters) {
    throw new Error(`Calibration profile not found: ${modelId}`);
  }

  return cameraParameters;
};

/**
 * Fetch the available device models for the specified brand.
 *
 * @param brandId - Brand whose models are to be returned.
 * @param token - Access token.
 * @param deviceType - Device type (default `Camera`).
 * @returns Models collection.
 */
export const fetchModels = async (
  brandId: Brand['id'],
  token: string,
  deviceType: DeviceType = DeviceType.Camera
): Promise<Collection<Model>> => {
  const { models } = await wrapApiCall(async () =>
    DeviceRegistryModelsList.request(nats.cloudConnection(), {
      brandId: toIdFromHex(brandId)!,
      deviceType,
      token
    })
  );

  models.sort(comparator);

  let generic: T_Maybe<Model>;

  const entries = transformArray(models, {
    transformer: (µ): T_Maybe<[Model['id'], Model]> => {
      const model = toModel(µ);

      if (!model) {
        return undefined;
      }

      // ENG-4924: a not-yet-supported model is also `generic` but is not the brand template.
      if (isGenericTemplate(µ)) {
        generic = model;
      }

      return [model.id, model];
    }
  });

  return { generic: ensureNotNil(generic), items: Object.fromEntries(entries) };
};

/**
 * Fetch the registrant for the specified device model.
 *
 * @param modelId - Model identifier.
 * @param token - Access token.
 * @returns Registry entry.
 */
export const fetchRegistrant = async (modelId: Model['id'], token: string): Promise<Registrant> => {
  // ENG-4012 — second unguarded site found by the stabilization review: the
  // configuration fetch leaks the same nil modelId (see requireModelIdBytes).
  const modelIdBytes = requireModelIdBytes(modelId, 'Device configuration');

  const {
    configurations: [config],
    model
  } = await wrapApiCall(async () =>
    DeviceRegistryGetConfiguration.request(nats.cloudConnection(), {
      modelId: modelIdBytes,
      token
    })
  );

  if (!model || !config) {
    throw new Error(`Device configuration not found: ${modelId}`);
  }

  // Clear control config for generic models
  if (model.generic) {
    config.ptzDriver = '';
  }

  return { config, model };
};

/**
 * Convert a protobuf brand to a Studio brand.
 *
 * @param pbBrand - Protobuf brand definition.
 * @returns Studio brand definition or `undefined` if the conversion failed.
 */
export function toBrand(pbBrand: OmitSyncProps<DeviceRegistryBrand>): T_Maybe<Brand> {
  const id = toHexFromId<Brand['id']>(pbBrand.id);
  const name = toString(pbBrand.name);

  return id && name ? { id, name } : undefined;
}

/**
 * Convert a protobuf model to a Studio model.
 *
 * @param pbModel - Protobuf model definition.
 * @returns Studio model definition or `undefined` if the conversion failed.
 */
export function toModel(pbModel: DeviceRegistryModel): T_Maybe<Model> {
  const modelId = toHexFromId<Model['id']>(pbModel.id);
  const brandId = toHexFromId<Model['brandId']>(pbModel.brandId);
  const name = toString(pbModel.name);
  const fidelity = pbModel.fidelityLabel;
  const iconUrl = toString(pbModel.iconUrl);
  // ENG-4495 C1: carry the driver-identity bit onto the Studio Model. `?? false` here is on the
  // PROTO SCALAR (the data source): proto3 bool decodes as present, and an older payload that omits
  // it decodes as `false` — which equals today's behavior. This is DISTINCT from the D3 tri-state
  // ban on `?? false` in consumers (which is about the *unresolved template*, not the proto field).
  const generic = pbModel.generic ?? false;
  // ENG-4924: same proto-scalar default as `generic`. An older registry that omits the field
  // decodes as `false`, which means every model is treated as supported, as before.
  const notYetSupported = pbModel.notYetSupported ?? false;

  return modelId && brandId && name
    ? { brandId, id: modelId, name, fidelity, iconUrl: iconUrl ?? '', generic, notYetSupported }
    : undefined;
}

/**
 * Convert a fidelity label enum value to a human-readable string.
 *
 * @param fidelityLabel - The fidelity label enum value.
 * @returns Human-readable fidelity label string.
 */
export function fidelityLabelToString(fidelityLabel: DeviceRegistryModelFidelityLabel): string {
  switch (fidelityLabel) {
    case DeviceRegistryModelFidelityLabel.FidelityLabel_Unknown:
      return 'Unknown';
    case DeviceRegistryModelFidelityLabel.FidelityLabel_Alpha:
    case DeviceRegistryModelFidelityLabel.FidelityLabel_Beta:
      return 'Beta';
    default:
      return 'Unknown';
  }
}
